import React, { forwardRef, ForwardRefRenderFunction } from "react";
//#region types
import { FormHandles } from "models/propTypes/genericFormProps";
import { AnnualAccountFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { CurrencyInputProps } from "models/propTypes/wintFormPropsTypes";
import { CurrencyCode } from "models/enums/currency";
import { AnnualAccountApproveFormViewProps } from "models/propTypes/annualAccountPropTypes";
import { SelectionItem } from "models/utilTypes/selectionTypes";
//#endregion
//#region components
import { CurrencyInput, InlineLabel, useWintForm, VerticalFormField, WintForm } from "components/common/wintForms";
import ToggleSwitch from "components/common/forms/baseFields/toggleSwitch";
import AnnualReportToApproveInformation from "../annualReportToApprove/informationText";
import { Grid } from "@mui/material";
import DividendSpaceList from "../annualReportToApprove/dividendSpaceList";
import DividendSpaceWarning from "../annualReportToApprove/dividendSpaceWarning";
import DividendSuggestionsField from "./fields/dividendSuggestionsField";
import PreliminaryYearResultWarning from "../annualReportToApprove/preliminaryYearResultWarning";
import WintNotHandleK10Warning from "../annualReportToApprove/wintNotHandleK10Warning";
import { useCheckRelations } from "components/common";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";

const AnnualAccountApproveForm: ForwardRefRenderFunction<FormHandles, AnnualAccountApproveFormViewProps> = (
  props,
  ref
) => {
  const {
    enableDividend,
    dividendInformation,
    financialYearId,
    isAtCustomerApproval,
    showK10Toggle,
    hasPartialK10,
    isK10OrderMode,
    onK10OrderModeChange,
    onDividendChange,
  } = props;

  const { getIsReadonlyUser, getIsRevisor, getIsAuditorAssistant } = useCheckRelations();
  const isReadOnly = !isAtCustomerApproval && getIsReadonlyUser();
  const isAuditorView = getIsRevisor() || getIsAuditorAssistant();

  const noDividendMessageKey = !enableDividend && !isAuditorView
    ? (dividendInformation?.annualReportNonRestrictedEquity ?? 0) <= 0
      ? TranslationKeys.AnnualAccount.NoDividendNoEquity
      : TranslationKeys.AnnualAccount.NoDividendAllowanceConsumed
    : undefined;

  const { wintFormProps, connect } = useWintForm<AnnualAccountFormTypes.Approve>({
    formRef: ref,
    defaultValues: props.defaultValues,
    validationSchema: props.validationSchema,
    handleSubmit: props.handleSubmit,
  });

  const dividendAmountProps = connect("amount");
  const isImmediateRef = React.useRef(false);
  const prevValueRef = React.useRef(dividendAmountProps.value);

  React.useEffect(() => {
    if (isK10OrderMode && dividendAmountProps.value !== undefined) {
      isImmediateRef.current = true;
      dividendAmountProps.setValue(undefined);
    }
  }, [isK10OrderMode, dividendAmountProps.value, dividendAmountProps.setValue]);

  React.useEffect(() => {
    if (prevValueRef.current !== dividendAmountProps.value) {
      const isImmediate = isImmediateRef.current;
      isImmediateRef.current = false;
      prevValueRef.current = dividendAmountProps.value;
      onDividendChange?.(dividendAmountProps.value, isImmediate);
    }
  }, [dividendAmountProps.value, onDividendChange]);

  const handleSuggestionSelect = React.useCallback(
    (value: SelectionItem) => {
      isImmediateRef.current = true;
      dividendAmountProps.setValue(value.data);
    },
    [dividendAmountProps.setValue]
  );

  const hasDividendProposal = dividendInformation?.dividendProposal !== undefined;

  return (
    <WintForm {...wintFormProps}>
      {enableDividend ? (
        <Grid container rowGap={3}>
          {dividendInformation ? <AnnualReportToApproveInformation dividendInformation={dividendInformation} /> : <></>}
          <Grid item container>
            <VerticalFormField<CurrencyInputProps>
              Component={CurrencyInput}
              {...dividendAmountProps}
              showMainLabel
              controlLabel={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.AnnualAccount.DividendLabel)}
              label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.AnnualAccount.Dividend)}
              currency={CurrencyCode.SEK}
              decimalPoints={0}
              allowNegative={false}
              disabled={isReadOnly || isK10OrderMode}
            />
            <Grid item container marginTop={-0.5} rowGap={2}>
              {isReadOnly ? (
                <></>
              ) : (
                <DividendSuggestionsField
                  dividendProposal={dividendInformation?.dividendProposal}
                  maxAllowedDividend={dividendInformation?.maxAllowedDividend}
                  setValue={handleSuggestionSelect}
                />
              )}
              <DividendSpaceWarning
                dividendProposal={dividendInformation?.dividendProposal}
                dividendAmount={dividendAmountProps.value}
              />
              <PreliminaryYearResultWarning
                preliminaryYearResult={dividendInformation?.preliminaryYearResult}
                maxAllowedDividend={dividendInformation?.maxAllowedDividend}
                dividendAmount={dividendAmountProps.value}
              />
              <WintNotHandleK10Warning
                hasDividendProposal={hasDividendProposal}
                dividendAmount={dividendAmountProps.value}
              />
            </Grid>
          </Grid>
          {showK10Toggle && (
            <Grid container direction="column" rowGap={1}>
              <Grid container direction="row" wrap="nowrap">
                <InlineLabel
                  columns
                  descriptionText={LocalizationHelperInstance.getFormattedMessage(
                    TranslationKeys.AnnualAccount.K10OrderToggleSubText
                  )}
                >
                  {LocalizationHelperInstance.getFormattedMessage(
                    hasPartialK10
                      ? TranslationKeys.AnnualAccount.K10OrderToggleLabelPartial
                      : TranslationKeys.AnnualAccount.K10OrderToggleLabelNone
                  )}
                </InlineLabel>
                <Grid item>
                  <ToggleSwitch
                    checked={isK10OrderMode ?? false}
                    onChange={(e) => onK10OrderModeChange?.(e.target.checked)}
                    disabled={isReadOnly}
                  />
                </Grid>
              </Grid>
            </Grid>
          )}
          <DividendSpaceList dividend={dividendAmountProps.value ?? 0} financialYearId={financialYearId} />
        </Grid>
      ) : noDividendMessageKey ? (
        LocalizationHelperInstance.getFormattedMessage(noDividendMessageKey)
      ) : (
        <></>
      )}
    </WintForm>
  );
};

export default forwardRef(AnnualAccountApproveForm);
